<script setup lang="ts">
import type { TOperationDto } from "@/types/backoffice/dto/operation/operation-dto";

import { useQuery } from "@tanstack/vue-query";
import { computed } from "vue";

import BackofficeApi from "@/api/backoffice/backoffice-api.ts";
import { OperatorQueryKeys } from "@/api/backoffice/operator/operator-query-keys.ts";
import { DATA_TEST_IDS } from "@/constants/data-test-ids";
import { chain } from "@/lib/chain";

import CascadeSelect from "primevue/cascadeselect";
import Skeleton from "primevue/skeleton";

interface IOperationGroup {
  name: string;
  code: string;
  operations: ReadonlyArray<TOperationDto>;
}

export interface TOperationCascadeSelectProps {
  id: string;
  disabled?: boolean;
  name?: string;
  placeholder?: string;
  panelTestId?: string;
  parentOptionTestId?: (name: string) => string;
  childOptionTestId?: () => string;
}

const props = defineProps<TOperationCascadeSelectProps>();
const model = defineModel<TOperationDto>();

const { data: operations, isLoading: isOperationsLoading } = useQuery({
  queryKey: OperatorQueryKeys.findAllOperations(),
  queryFn: () => BackofficeApi.findOperations({ queries: { isOpened: true } }),
});

const allOperations = computed<Array<TOperationDto>>(() => {
  const apiReturnedOperations = operations.value ?? [];
  const currentValue = model.value;

  const isCurrentValueInApiResults = currentValue && apiReturnedOperations.some((op) => op.id === currentValue.id);

  if (!currentValue || isCurrentValueInApiResults) {
    return apiReturnedOperations;
  }

  return [currentValue, ...apiReturnedOperations];
});

const groupedOperations = computed<Array<IOperationGroup>>(() =>
  chain(allOperations.value)
    .groupBy((op) => op.type.name ?? "undefined")
    .entries()
    .map(([typeName, operations]) => ({
      name: typeName,
      code: typeName,
      operations,
    }))
    .value(),
);

const cascadeSelectPt = computed(() =>
  props.panelTestId
    ? {
        panel: {
          "data-test-id": props.panelTestId,
        },
      }
    : undefined,
);

function isOperationGroup(option: unknown): option is IOperationGroup {
  return typeof option === "object" && option !== null && "operations" in option;
}
</script>

<template>
  <Skeleton
    v-if="isOperationsLoading"
    class="w-full h-full"
    style="height: 3rem"
    :data-test-id="DATA_TEST_IDS.components.operationCascadeSelect.loadingSkeleton()"
  />
  <CascadeSelect
    v-else
    :id="props.id"
    v-model="model"
    data-key="id"
    :loading="isOperationsLoading"
    :disabled="props.disabled"
    :options="groupedOperations"
    :name="props.name"
    option-label="name"
    option-group-label="name"
    option-group-children="operations"
    :placeholder="props.placeholder"
    class="w-full"
    v-bind="$attrs"
    :data-test-id="DATA_TEST_IDS.components.operationCascadeSelect.component"
    :pt="cascadeSelectPt"
  >
    <template #option="{ option }">
      <template v-if="isOperationGroup(option)">
        <div
          v-if="props.parentOptionTestId"
          :data-test-id="props.parentOptionTestId(option.name)"
        >
          {{ option.name }}
        </div>
        <div v-else>
          {{ option.name }}
        </div>
      </template>
      <template v-else>
        <div
          v-if="props.childOptionTestId"
          :data-test-id="props.childOptionTestId()"
        >
          {{ option.name }}
        </div>
        <div v-else>
          {{ option.name }}
        </div>
      </template>
    </template>
  </CascadeSelect>
</template>
